import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import {EnergyMapConstants} from '../energy.map.constants';
import {PortalService} from '../../services/portal.service';
import {DebugService} from '../../debug/debug.service';

/**
 * Service for speaking with the energy-map.
 */
@Injectable({
  providedIn: 'root'
})
export class EnergyMapService {
  /**
   * The base URL for energy-map endpoints
   */
  apiEnergyMapUrl = EnergyMapConstants.berlinMapBaseUrl + '/api';

  /**
   * Default country
   */
  defaultCountry = 'Germany';

  /**
   * constructor.
   * @param http
   * @param portalService
   * @param debugService
   */
  constructor(protected http: HttpClient, protected portalService: PortalService, protected debugService: DebugService) {
  }

  /**
   * Sends a request to the energy-map and returns the response data.
   * @param url
   * @param method
   * @param data
   * @protected
   */
  protected doRequest(url: string, method: string, data: any = {}): Observable<any> {
    if (method === 'get') {
      return this.http.get(this.apiEnergyMapUrl + url, {params: data}).pipe(
        catchError((error) => {
          // an error occurred while getting the data from the backend
          console.error("EnergyMapService: Error while getting data from the energy-map.", error);
          return of(error.hasOwnProperty('error') ? error.error : error);
        })
      );
    }
    return this.http[method](this.apiEnergyMapUrl + url, data).pipe(
      catchError((error) => {
        // an error occurred while getting the data from the backend
        console.error("EnergyMapService: Error while getting data from the energy-map.", error);
        return of(error.hasOwnProperty('error') ? error.error : error);
      })
    );
  }

  /**
   * Search by country, postalcode, state, county, city, street, house_number
   * @param data
   */
  search(data: any = {}) {
    if (data.hasOwnProperty('country') === false) {
      data.country = this.defaultCountry;
    }
    return this.doRequest('/search', 'get', data);
  }

  /**
   * Get list of streets by postalcode
   * @param data
   */
  streets(data: any = {}) {
    return this.doRequest('/streets', 'get', data);
  }

  /**
   * Returns whether Energy Map should be displayed or not
   */
  showEnergyMap(anfrage: any = {}, advisorName = ''): boolean {
    let result = false;
    if (this.portalService.getConfigAsBool(advisorName + '.energy.map.show') === true) {
      switch (this.getPath(anfrage, advisorName)) {
        case 'heating_bill':
          result = EnergyMapConstants.isBerlinPostalCode((anfrage?.heizkostenanalyseInput?.postleitzahl).toString()) === true
            && anfrage?.heizkostenanalyseInput?.heizungstyp === 'zentral';
          break;
        case 'energy_bill':
        default:
          result = EnergyMapConstants.isBerlinPostalCode((anfrage?.verbrauchsanalyseInput?.postleitzahl).toString()) === true
            && anfrage?.verbrauchsanalyseInput?.heizungstyp === 'zentral';
          break;
      }
    }
    return result;
  }

  isEnergyMapDataDonated(anfrage: any = {}) {
    return anfrage?.verbrauchsanalyseInput?.energy_map_data_donated?.length > 0;
  }

  getEnergyMapConfirmDonation(anfrage: any = {}) {
    return anfrage?.verbrauchsanalyseInput?.energy_map_confirm_donation ?
        anfrage?.verbrauchsanalyseInput?.energy_map_confirm_donation : null;
  }

  getEnergyMapBuildingNotIncludedInMap(anfrage: any = {}) {
    return anfrage?.verbrauchsanalyseInput?.energy_map_building_not_included_in_map === true;
  }

  getPath(anfrage: any = {}, advisorName = ''): string {
    if (advisorName === 'heizcheck'
      && (anfrage?.verbrauchsanalyseInput?.nutzungstyp === 'mieter' || anfrage?.verbrauchsanalyseInput?.nutzungstyp === 'wohnungseigentuemer')
      && anfrage?.verbrauchsanalyseInput?.heizungstyp === 'zentral'
      && anfrage?.verbrauchsanalyseInput?.gebaeudetyp === 'mehrfamilienhaus') {
      return 'heating_bill';
    }
    else {
      return 'energy_bill';
    }
  }

  getPostleitzahl(anfrage: any = {}, advisorName = ''): string {
    let postleitzahl: string;
    if (this.getPath(anfrage, advisorName) === 'heating_bill') {
      postleitzahl = anfrage?.heizkostenanalyseInput?.postleitzahl ? anfrage.heizkostenanalyseInput.postleitzahl : '';
    }
    else {
      postleitzahl = anfrage?.verbrauchsanalyseInput?.postleitzahl ? anfrage.verbrauchsanalyseInput.postleitzahl : '';
    }
    return postleitzahl;
  }

  getGebaeudeStockwerke(anfrage: any = {}, advisorName = ''): number {
    let result: number;
    if (this.getPath(anfrage, advisorName) === 'heating_bill') {
      result = anfrage?.heizkostenanalyseInput?.gebaeude_stockwerke ? anfrage.heizkostenanalyseInput.gebaeude_stockwerke : 0;
    }
    else {
      result = anfrage?.verbrauchsanalyseInput?.gebaeude_stockwerke ? anfrage.verbrauchsanalyseInput.gebaeude_stockwerke : 0;
    }
    return result;
  }

  /**
   * Donate data
   * @param input
   * @param requestId
   */
  datadonation(input: any, requestId: string){
    const url = '/datadonation/' + requestId;

    // add debug header if we are in debug mode
    const headers = new HttpHeaders({
      'x-api-debug': this.debugService.isDebugOn() ? '1' : '0'
    });

    this.http.post(url, input, {headers}).subscribe(result => {
      if (this.debugService.isDebugOn()) {
        console.log(result);
      }
    }, error => {
      console.error('an error occurred while sending the data to energy bill data donation');
      console.log(error);
    });
  }

}
